import fetch from 'cross-fetch'

import { getTokenInLocaleStorage } from './general'
import { message } from 'antd'

let isLoggingOut = false
let abortController = new AbortController()

function handleResponse(userType) {
  return function (response) {
    // ---- 401 : Session expired ----
    if (response.status === 401) {
      if (!isLoggingOut) {
        isLoggingOut = true

        abortController.abort()
        abortController = new AbortController()

        localStorage.clear()
        message.error('Session Expired')

        if (!window.location.pathname.includes('/login')) {
          const loginPath = `/${
            userType === 'learnInvest'
              ? 'education/learn-invest'
              : userType === 'series65'
              ? 'education/get-accredited'
              : userType
          }/login`

          window.location.replace(loginPath)
        }
        // return Promise.reject({ status: 401, message: 'Unauthorized' })
        return new Promise(() => {})
      }
    }

    // ---- 403 : Suspended / forbidden ----
    if (response.status === 403) {
      if (!window.location.pathname.includes('/suspend')) {
        window.location.replace('/suspend')
      }

      // return Promise.reject({ status: 403, message: 'Forbidden' })
      return new Promise(() => {})
    }

    return response.json()
  }
}

/**
 * Send a JSON request with optional token authentication.
 * @param {string} method - The HTTP method (GET, POST, PATCH, etc.).
 * @param {string} url - The URL to send the request to.
 * @param {object} body - The request body in JSON format.
 * @param {boolean} withToken - Flag indicating whether to include authentication token in the request.
 * @returns {Promise} - A Promise that resolves to the JSON response.
 */
function sendRequest(method, url, body, withToken, userType) {
  const token = getTokenInLocaleStorage(userType)
  if (withToken && !token) {
    return Promise.reject({ status: 401, message: 'No token' })
  }
  const headers = {
    'Content-Type': 'application/json',
  }
  if (withToken) {
    headers['Authorization'] = 'Bearer ' + token
  }

  if (body) {
    return fetch(url, {
      method,
      headers,
      body: JSON.stringify(body),
    }).then(handleResponse(userType))
  } else {
    return fetch(url, {
      method,
      headers,
    }).then(handleResponse(userType))
  }
}

/**
 * Send a form data request with optional token authentication.
 * @param {string} method - The HTTP method (GET, POST, PATCH, etc.).
 * @param {string} url - The URL to send the request to.
 * @param {FormData} body - The request body in form data format.
 * @param {boolean} withToken - Flag indicating whether to include authentication token in the request.
 * @returns {Promise} - A Promise that resolves to the JSON response.
 */
function sendFormDataRequest(method, url, body, withToken, userType) {
  const token = getTokenInLocaleStorage(userType)

  if (withToken && !token) {
    return Promise.reject({ status: 401, message: 'No token' })
  }
  const headers = {}
  headers['Authorization'] = 'Bearer ' + token

  if (body) {
    return fetch(url, {
      method,
      headers,
      body: body,
    }).then(handleResponse(userType))
  } else {
    return fetch(url, {
      method,
      headers,
    }).then(handleResponse(userType))
  }
}

/**
 * Send a form data request with optional token authentication.
 * @param {string} method - The HTTP method (GET, POST, PATCH, etc.).
 * @param {string} url - The URL to send the request to.
 * @param {FormData} body - The request body in form data format.
 * @param {boolean} withToken - Flag indicating whether to include authentication token in the request.
 * @returns {Promise} - A Promise that resolves to the JSON response.
 */
function sendRequestForm(method, url, body, withToken, userType) {
  const token = getTokenInLocaleStorage(userType)

  if (withToken && !token) {
    return Promise.reject({ status: 401, message: 'No token' })
  }

  const headers = {}
  headers['Authorization'] = 'Bearer ' + token

  if (body) {
    return fetch(url, {
      method,
      headers,
      body: body,
    }).then(handleResponse(userType))
  } else {
    return fetch(url, {
      method,
      headers,
    }).then(handleResponse(userType))
  }
}

/**
 * Send a file upload request.
 * @param {string} url - The URL to send the request to.
 * @param {File} file - The file to upload.
 * @param {string} fileType - The type of the file being uploaded.
 * @returns {Promise} - A Promise that resolves to the JSON response.
 */
function sendFile(url, file, fileType, userType) {
  const token = getTokenInLocaleStorage(userType)

  if (!token) {
    return Promise.reject({ status: 401, message: 'No token' })
  }
  const headers = {
    // Do not specify Content-Type. See https://stackoverflow.com/a/49510941/10057192
    Authorization: 'Bearer ' + token,
  }
  const formData = new FormData()
  formData.append('file_obj', file)
  formData.append('type', fileType)
  return fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  }).then(handleResponse(userType))
}

// Exporting an object with various HTTP request functions
export default {
  getWithoutToken: (url, body) => {
    return sendRequest('GET', url, body, false)
  },
  post: (url, body, userType, withToken = true) => {
    return sendRequest('POST', url, body, withToken, userType)
  },
  postwithFormData: (url, body, userType) => {
    return sendFormDataRequest('POST', url, body, true, userType)
  },
  postWithoutToken: (url, body, userType, withToken = false) => {
    return sendRequest('POST', url, body, withToken, userType)
  },
  patch: (url, body, userType) => {
    return sendRequest('PATCH', url, body, true, userType)
  },
  get: (url, body, userType) => {
    return sendRequest('GET', url, body, true, userType)
  },
  postwithReqForm: (url, body, userType) => {
    return sendRequestForm('POST', url, body, true, userType)
  },
  getwithReqForm: (url, body, userType) => {
    return sendRequestForm('GET', url, body, true, userType)
  },
  sendFile,
}
